/**
 * Brand file-type icons for output formats — modern "document with a coloured
 * type-band" style (the look file explorers / VS Code use): a white page with a
 * folded corner and a coloured label band (PPTX / DOCX / PDF / FORM / MD) in
 * each product's brand colour. Self-contained SVG, no external icon dependency
 * (Office product logos aren't shipped by icon packs and are trademarked).
 */

import React from 'react';
import type { OutputFormat } from '@/types/models';

interface IconProps {
  size?: number;
}

const SPEC: Record<OutputFormat, { color: string; label: string; fontSize: number }> = {
  pptx: { color: '#C43E1C', label: 'PPTX', fontSize: 4.0 },
  docx: { color: '#2B579A', label: 'DOCX', fontSize: 4.0 },
  xlsx: { color: '#217346', label: 'XLSX', fontSize: 4.0 },
  html: { color: '#E5392E', label: 'PDF', fontSize: 4.8 },
  // Adobe red, the colour every reader associates with the format.
  pdf: { color: '#E5392E', label: 'PDF', fontSize: 4.8 },
  gforms: { color: '#7248B9', label: 'FORM', fontSize: 4.2 },
  markdown: { color: '#475569', label: 'MD', fontSize: 5.0 },
  // Apple Numbers — its app's green.
  numbers: { color: '#1DA34B', label: 'NUM', fontSize: 4.6 },
  zip: { color: '#6B7280', label: 'ZIP', fontSize: 4.6 },
};

const FileTypeGlyph: React.FC<IconProps & { color: string; label: string; fontSize: number }> = ({
  size = 24,
  color,
  label,
  fontSize,
}) => (
  <svg width={size} height={size} viewBox="0 0 24 24" fill="none" aria-hidden="true">
    {/* page */}
    <path
      d="M6 2.5h8.2L19 7.3V20a1.5 1.5 0 0 1-1.5 1.5h-11A1.5 1.5 0 0 1 5 20V4a1.5 1.5 0 0 1 1-1.5z"
      fill="#ffffff"
      stroke="#d6dbe1"
      strokeWidth="1"
      strokeLinejoin="round"
    />
    {/* folded corner */}
    <path
      d="M14.2 2.5V7.3H19"
      fill="#eef1f4"
      stroke="#d6dbe1"
      strokeWidth="1"
      strokeLinejoin="round"
    />
    {/* coloured type band */}
    <rect x="2.5" y="12.4" width="14" height="6.8" rx="1.4" fill={color} />
    <text
      x="9.5"
      y="15.95"
      textAnchor="middle"
      dominantBaseline="central"
      fontFamily="Arial, Helvetica, sans-serif"
      fontWeight="700"
      fontSize={fontSize}
      fill="#ffffff"
    >
      {label}
    </text>
  </svg>
);

export const OutputFormatIcon: React.FC<IconProps & { format: OutputFormat }> = ({
  format,
  ...rest
}) => {
  const spec = SPEC[format];
  return spec ? <FileTypeGlyph {...rest} {...spec} /> : null;
};

export default OutputFormatIcon;
